import React from 'react';

import Image from 'next/legacy/image';
import { usePathname } from 'next/navigation';

import { withEventScope } from '@repo/event-processor';
import { primaryTheme } from '@repo/ui/components/Buttons';
import { Spacer } from '@repo/ui/components/Spacer';
import { useTheme } from '@repo/ui/theming';

import Layout from 'components/Layout/Layout';
import LinkButton from 'components/LinkButton/LinkButton';
import { Container, ContentWrapper } from 'components/utilities/pageStyles';
import { FlexColumn, FlexRow } from 'styles/layouts';
import { HideMobile } from 'styles/queries';

import { Divider, Title, Subtitle } from './styles';

interface ButtonData {
  buttonLabel: string;
  buttonHref: string;
}

export interface ErrorPageProps extends Partial<ButtonData> {
  status?: ErrorStatus;
}

interface URLData extends Required<ButtonData> {
  path: string;
}

const DEFAULT_BUTTON_DATA: ButtonData = {
  buttonLabel: 'Back to Home',
  buttonHref: '/',
};

const URLS: URLData[] = [
  {
    path: '/challenges/',
    buttonLabel: 'See Challenges',
    buttonHref: '/challenges',
  },
  {
    path: '/article/',
    buttonLabel: 'Back to Blog',
    buttonHref: '/blog',
  },
  {
    path: '/help/',
    buttonLabel: 'Back to Help',
    buttonHref: '/help',
  },
];

export const enum ErrorStatus {
  NotFound = 404,
}

const getErrorData = (
  status?: ErrorStatus
): Record<'title' | 'subtitle', string> => {
  if (status === ErrorStatus.NotFound) {
    return {
      title: '404',
      subtitle: 'Page Not Found',
    };
  }
  return {
    title: '500',
    subtitle: 'Something Went Wrong',
  };
};

const ErrorPage: React.FC<ErrorPageProps> = (props) => {
  const [btnInfo, setBtnInfo] = React.useState<Required<ButtonData>>();
  const pathname = usePathname();
  const { title, subtitle } = getErrorData(props.status);

  React.useEffect(() => {
    if (
      props.buttonHref &&
      props.buttonLabel &&
      (DEFAULT_BUTTON_DATA.buttonHref !== props.buttonHref ||
        DEFAULT_BUTTON_DATA.buttonLabel !== props.buttonLabel)
    ) {
      setBtnInfo({
        buttonLabel: props.buttonLabel,
        buttonHref: props.buttonHref,
      });
    } else {
      const { buttonLabel, buttonHref } =
        URLS.find(({ path }) => pathname?.startsWith(path)) ||
        DEFAULT_BUTTON_DATA;
      setBtnInfo({ buttonLabel, buttonHref });
    }
  }, [pathname, props]);

  const { isDarkTheme } = useTheme();

  return (
    <Layout>
      <Container>
        <ContentWrapper>
          <Spacer height="75px" />
          <FlexRow alignItems="center" gap="75px" justifyContent="center">
            <FlexColumn alignItems="center">
              <Title as="h2">{title}</Title>
              <Subtitle>{subtitle}</Subtitle>
              <Spacer height="75px" />
              {/* Need to render Spacer to avoid shifting of the interface */}
              {btnInfo ? (
                <LinkButton
                  height="40px"
                  href={btnInfo.buttonHref}
                  label={btnInfo.buttonLabel}
                  theme={primaryTheme}
                  width="150px"
                />
              ) : (
                <Spacer height="40px" />
              )}
            </FlexColumn>
            <HideMobile>
              <FlexRow>
                <Divider />
                <Image
                  alt="Page Not Found"
                  height="280"
                  src={
                    isDarkTheme
                      ? '/images/newTheme/dark/Empty.png'
                      : '/images/newTheme/light/Empty.png'
                  }
                  width="350"
                />
              </FlexRow>
            </HideMobile>
          </FlexRow>
          <Spacer height="50px" />
        </ContentWrapper>
      </Container>
    </Layout>
  );
};

export default withEventScope(ErrorPage, { page: 'errorPage' });
